import React from 'react';
import {
  Box,
  HStack,
  Stack,
  Text,
  Image,
  NumberInput,
  NumberInputField,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { Match } from '../../types';
import { toDate } from '../../services/matchdayService';
import { useAppColors } from '../../hooks/useAppColors';

interface MatchPickCardProps {
  match: Match;
  teamLogos?: Record<string, string>;
  scoreA?: number;
  scoreB?: number;
  onScoreChange: (team: 'a' | 'b', value: number) => void;
  disabled?: boolean;
  locked?: boolean;
  closed?: boolean;
}

const ScoreInputs: React.FC<{
  scoreA?: number;
  scoreB?: number;
  onScoreChange: (team: 'a' | 'b', value: number) => void;
  disabled?: boolean;
  locked?: boolean;
  closed?: boolean;
  inputBg: string;
  inputBorder: string;
  focusBorder: string;
  text: string;
  separatorColor: string;
}> = ({
  scoreA,
  scoreB,
  onScoreChange,
  disabled,
  locked,
  closed,
  inputBg,
  inputBorder,
  focusBorder,
  text,
  separatorColor,
}) => (
  <HStack spacing={2} flexShrink={0} justify="center">
    <NumberInput
      min={0}
      max={20}
      w={{ base: '56px', sm: '52px' }}
      value={scoreA ?? ''}
      clampValueOnBlur
      onChange={(_, v) => onScoreChange('a', Number.isNaN(v) ? 0 : v)}
      isDisabled={disabled || locked || closed}
    >
      <NumberInputField
        textAlign="center"
        fontWeight="bold"
        fontSize="lg"
        h="40px"
        px={2}
        bg={inputBg}
        borderWidth="2px"
        borderColor={inputBorder}
        color={text}
        borderRadius="md"
        inputMode="numeric"
        pattern="[0-9]*"
        _focus={{
          borderColor: focusBorder,
          boxShadow: `0 0 0 1px ${focusBorder}`,
        }}
      />
    </NumberInput>
    <Text fontWeight="bold" color={separatorColor}>
      -
    </Text>
    <NumberInput
      min={0}
      max={20}
      w={{ base: '56px', sm: '52px' }}
      value={scoreB ?? ''}
      clampValueOnBlur
      onChange={(_, v) => onScoreChange('b', Number.isNaN(v) ? 0 : v)}
      isDisabled={disabled || locked || closed}
    >
      <NumberInputField
        textAlign="center"
        fontWeight="bold"
        fontSize="lg"
        h="40px"
        px={2}
        bg={inputBg}
        borderWidth="2px"
        borderColor={inputBorder}
        color={text}
        borderRadius="md"
        inputMode="numeric"
        pattern="[0-9]*"
        _focus={{
          borderColor: focusBorder,
          boxShadow: `0 0 0 1px ${focusBorder}`,
        }}
      />
    </NumberInput>
  </HStack>
);

export const MatchPickCard: React.FC<MatchPickCardProps> = ({
  match,
  teamLogos,
  scoreA,
  scoreB,
  onScoreChange,
  disabled,
  locked,
  closed,
}) => {
  const { muted, text, inputBg, inputBorder } = useAppColors();
  const bg = useColorModeValue('white', 'slate.850');
  const border = useColorModeValue('gray.200', 'gray.700');
  const focusBorder = useColorModeValue('pitch.500', 'pitch.400');
  const separatorColor = useColorModeValue('gray.400', 'gray.500');
  const kickoff = toDate(match.date).toLocaleString('es-MX', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  const scoreProps = {
    scoreA,
    scoreB,
    onScoreChange,
    disabled,
    locked,
    closed,
    inputBg,
    inputBorder,
    focusBorder,
    text,
    separatorColor,
  };

  return (
    <Box
      bg={bg}
      borderWidth="1px"
      borderColor={locked ? 'pitch.500' : border}
      borderRadius="lg"
      p={{ base: 3, sm: 4 }}
      opacity={disabled || closed ? 0.65 : 1}
    >
      <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Text fontSize="xs" color={muted} fontWeight="600">
          {kickoff}
        </Text>
        {locked && (
          <Badge colorScheme="green" variant="subtle">
            Capturado
          </Badge>
        )}
        {closed && (
          <Badge colorScheme="red" variant="subtle">
            Cerrado
          </Badge>
        )}
        {match.group && (
          <Badge variant="outline" colorScheme="pitch">
            Grupo {match.group}
          </Badge>
        )}
      </HStack>

      {/* Móvil: equipos apilados con marcador al centro */}
      <Stack display={{ base: 'flex', sm: 'none' }} spacing={3} align="stretch">
        <HStack minW={0}>
          <Image
            src={teamLogos?.[match.team_a] || '/images/mundial_2026.png'}
            alt={match.team_a}
            boxSize="32px"
            objectFit="contain"
            flexShrink={0}
          />
          <Text fontWeight="bold" fontSize="sm" noOfLines={2} color={text}>
            {match.team_a}
          </Text>
        </HStack>
        <ScoreInputs {...scoreProps} />
        <HStack minW={0} justify="flex-end">
          <Text fontWeight="bold" fontSize="sm" noOfLines={2} textAlign="right" color={text}>
            {match.team_b || '—'}
          </Text>
          <Image
            src={teamLogos?.[match.team_b || ''] || '/images/mundial_2026.png'}
            alt={match.team_b || ''}
            boxSize="32px"
            objectFit="contain"
            flexShrink={0}
          />
        </HStack>
      </Stack>

      {/* Tablet/desktop: fila horizontal */}
      <HStack
        display={{ base: 'none', sm: 'flex' }}
        justify="space-between"
        align="center"
        spacing={3}
      >
        <HStack flex={1} minW={0}>
          <Image
            src={teamLogos?.[match.team_a] || '/images/mundial_2026.png'}
            alt={match.team_a}
            boxSize="36px"
            objectFit="contain"
            flexShrink={0}
          />
          <Text fontWeight="bold" fontSize="sm" noOfLines={2} color={text}>
            {match.team_a}
          </Text>
        </HStack>
        <ScoreInputs {...scoreProps} />
        <HStack flex={1} minW={0} justify="flex-end">
          <Text fontWeight="bold" fontSize="sm" noOfLines={2} textAlign="right" color={text}>
            {match.team_b || '—'}
          </Text>
          <Image
            src={teamLogos?.[match.team_b || ''] || '/images/mundial_2026.png'}
            alt={match.team_b || ''}
            boxSize="36px"
            objectFit="contain"
            flexShrink={0}
          />
        </HStack>
      </HStack>

      {closed && (
        <Text fontSize="xs" color="red.400" mt={2}>
          El partido ya inició; ya no se puede pronosticar.
        </Text>
      )}
    </Box>
  );
};
